import { useEffect } from 'react';
import { useClientTokenManager, useCurrentClientManager } from '@cko/dashboard-shared';

type CustomProperties = {
  CkoUserId?: string; // Prefixed with Cko to differentiate between Heap's and CKO's user id.
  ClientID?: string;
  ClientName?: string;
  EntityID?: string;
  EntityName?: string;
  Permissions: string;
  Role: string;
  SubEntityID?: string;
  SubEntityName?: string;
};

const useHeap = () => {
  const { tokenProperties } = useClientTokenManager();
  const { sub, permissions, userId, userRoles } = tokenProperties;

  const { client, entity, subEntity } = useCurrentClientManager();
  const { id: clientID, name: clientName } = client || {};
  const { id: entityID, name: entityName } = entity || {};
  const { id: subEntityID, name: subEntityName } = subEntity || {};

  const stringifiedPermissions = permissions.join();
  const stringifiedRoles = userRoles.join();

  const hasHeapLoaded = window.heap?.loaded;

  useEffect(() => {
    if (!hasHeapLoaded) {
      return;
    }

    try {
      window.heap?.identify(sub);
    } catch {
      // heap failed to init, we dont want to prevent the app from being useable so just eat the error.
    }
  }, [hasHeapLoaded, sub]);

  useEffect(() => {
    if (!hasHeapLoaded) {
      return;
    }

    const newProperties: CustomProperties = {
      CkoUserId: userId,
      ClientID: clientID,
      ClientName: clientName,
      EntityID: entityID,
      EntityName: entityName,
      Permissions: stringifiedPermissions,
      Role: stringifiedRoles,
      SubEntityID: subEntityID,
      SubEntityName: subEntityName,
    };

    try {
      window.heap?.addEventProperties(newProperties);
    } catch {
      // heap failed to init, we dont want to prevent the app from being useable so just eat the error.
    }
  }, [
    clientID,
    entityID,
    hasHeapLoaded,
    stringifiedPermissions,
    stringifiedRoles,
    subEntityID,
    userId,
  ]);
};

export default useHeap;
